import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Check, X, Coins, ArrowLeft } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const PaymentComplete = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');
  const [paymentDetails, setPaymentDetails] = useState<any>(null);

  useEffect(() => {
    const paymentIntent = searchParams.get('payment_intent');
    const paymentIntentClientSecret = searchParams.get('payment_intent_client_secret');
    const redirectStatus = searchParams.get('redirect_status');

    if (redirectStatus === 'succeeded') {
      setStatus('success');
      setMessage('Your payment has been processed successfully!');
      setPaymentDetails({
        paymentIntent,
        status: 'succeeded'
      });
      
      // Redirect to profile after 3 seconds
      setTimeout(() => {
        navigate('/profile');
      }, 3000);
    } else if (redirectStatus === 'failed') {
      setStatus('error');
      setMessage('Payment failed. Please try again.');
    } else {
      setStatus('error');
      setMessage('Invalid payment status. Please contact support if you believe this is an error.');
    }
  }, [searchParams, navigate]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Card className="w-full max-w-md">
            <CardContent className="text-center py-16">
              <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary mb-4" />
              <p className="text-lg font-medium mb-2">Processing Payment</p>
              <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center py-16">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-gradient-to-br from-green-100 to-green-200 dark:from-green-900 dark:to-green-800 rounded-full flex items-center justify-center mb-6">
                <Check className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <CardTitle className="text-2xl text-green-600 dark:text-green-400 mb-2">
                Payment Successful!
              </CardTitle>
              <CardDescription className="text-base">
                {message}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 rounded-lg p-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Payment Status:</span>
                  <span className="text-green-700 dark:text-green-300 capitalize">Completed</span>
                </div>
                {paymentDetails?.paymentIntent && (
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Payment ID:</span>
                    <span className="text-xs font-mono">{paymentDetails.paymentIntent}</span>
                  </div>
                )}
              </div>
              
              <div className="space-y-3">
                <p className="text-sm text-center text-muted-foreground">
                  Your tokens have been added to your account.
                </p>
                <p className="text-sm text-center text-muted-foreground">
                  Redirecting to your profile in a few seconds...
                </p>
                
                <div className="flex gap-2">
                  <Link to="/profile" className="flex-1">
                    <Button className="w-full">
                      <Coins className="w-4 h-4 mr-2" />
                      Go to Profile
                    </Button>
                  </Link>
                  <Link to="/create-guide">
                    <Button variant="outline">
                      Create Guide
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1 flex items-center justify-center py-16">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900 dark:to-red-800 rounded-full flex items-center justify-center mb-6">
              <X className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle className="text-2xl text-red-600 dark:text-red-400 mb-2">
              Payment Failed
            </CardTitle>
            <CardDescription className="text-base">
              {message}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertDescription>
                If you were charged but see this error, please contact our support team with your payment details.
              </AlertDescription>
            </Alert>
            
            <div className="flex gap-2">
              <Link to="/pricing" className="flex-1">
                <Button variant="outline" className="w-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline">
                  Contact Support
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default PaymentComplete;